class BacColorRotation extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.gallery = this.closest('.card-gallery');
    this.card = this.closest('product-card') || this.gallery;
    this.button = this.querySelector('button');
    this.caption = this.querySelector('[data-color-name]');
    this.items = JSON.parse(this.querySelector('script').textContent || '[]');
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.index = 0;
    this.visible = false;
    this.userPaused = false;
    this.interacted = false;
    this.delay = 5000 + Number(this.gallery.dataset.productId.slice(-2)) % 5 * 250;
    this.button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.userPaused = !this.userPaused;
      this.forcePlay = !this.userPaused;
      this.sync();
    }, { signal });
    this.card.addEventListener('pointerenter', () => { this.forcePlay = false; this.stop(); }, { signal });
    this.card.addEventListener('pointerleave', () => this.sync(), { signal });
    this.card.addEventListener('focusin', () => { this.forcePlay = false; this.stop(); }, { signal });
    this.card.addEventListener('focusout', () => queueMicrotask(() => { this.forcePlay = false; this.sync(); }), { signal });
    this.card.addEventListener('change', () => {
      this.interacted = true;
      this.caption.hidden = true;
      this.preview?.remove();
      this.preview = undefined;
      this.sync();
    }, { signal });
    this.card.addEventListener('pointerdown', (event) => {
      if (event.target.closest('slideshow-arrows, swatches-variant-picker-component')) {
        this.interacted = true;
        this.caption.hidden = true;
        this.preview?.remove();
        this.preview = undefined;
        this.sync();
      }
    }, { signal });
    this.card.addEventListener('click', (event) => {
      if (event.target.closest('slideshow-arrows')) {
        this.interacted = true;
        this.caption.hidden = true;
        this.preview?.remove();
        this.preview = undefined;
        this.sync();
      }
    }, { signal });
    document.addEventListener('visibilitychange', () => this.sync(), { signal });
    this.motion.addEventListener('change', () => this.sync(), { signal });
    this.observer = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      this.sync();
    }, { threshold: 0.3 });
    this.observer.observe(this.gallery);
    customElements.whenDefined('slideshow-component').then(() => {
      if (!this.isConnected) return;
      this.slideshow = this.gallery.querySelector('slideshow-component');
      this.items = this.items.filter((item) => this.slideshow?.querySelector(`slideshow-slide[slide-id="${item.mediaId}"]`));
      if (this.items.length < 2) return;
      const selected = this.items.findIndex((item) => this.slideshow.querySelector(`slideshow-slide[slide-id="${item.mediaId}"]`)?.getAttribute('aria-hidden') === 'false');
      this.index = Math.max(0, selected);
      this.caption.textContent = this.items[this.index].color;
      this.hidden = false;
      this.sync();
    });
  }

  stop() {
    clearTimeout(this.timer);
    this.timer = undefined;
  }

  sync() {
    this.stop();
    if (!this.button) return;
    this.button.setAttribute('aria-label', this.userPaused ? this.dataset.playLabel : this.dataset.pauseLabel);
    this.button.setAttribute('aria-pressed', String(this.userPaused));
    this.button.querySelector('[data-pause-icon]').hidden = this.userPaused;
    this.button.querySelector('[data-play-icon]').hidden = !this.userPaused;
    if (!this.slideshow || this.items.length < 2 || !this.visible || this.userPaused || this.interacted || this.motion.matches || document.hidden || (!this.forcePlay && this.card.matches(':hover, :focus-within'))) return;
    this.timer = setTimeout(() => this.advance(), this.delay);
  }

  async advance() {
    this.index = (this.index + 1) % this.items.length;
    const item = this.items[this.index];
    const slide = this.slideshow.querySelector(`slideshow-slide[slide-id="${item.mediaId}"]`);
    const image = slide?.querySelector('img');
    if (image) {
      image.loading = 'eager';
      try { await image.decode(); } catch { this.sync(); return; }
    }
    if (!this.isConnected || this.userPaused || this.interacted || this.motion.matches || document.hidden || !this.visible || (!this.forcePlay && this.card.matches(':hover, :focus-within'))) { this.sync(); return; }
    if (!image) { this.sync(); return; }
    const nextPreview = image.cloneNode(false);
    nextPreview.removeAttribute('id');
    nextPreview.removeAttribute('ref');
    nextPreview.classList.add('bac-color-preview');
    nextPreview.style.objectFit = getComputedStyle(image).objectFit;
    this.preview?.remove();
    this.preview = nextPreview;
    this.gallery.querySelector('[ref="cardGalleryLink"]').append(nextPreview);
    this.caption.textContent = item.color;
    this.dataset.currentColor = item.color;
    this.sync();
  }

  disconnectedCallback() {
    this.stop();
    this.controller?.abort();
    this.observer?.disconnect();
    this.preview?.remove();
    this.preview = undefined;
    this.controller = undefined;
  }
}

if (!customElements.get('bac-color-rotation')) customElements.define('bac-color-rotation', BacColorRotation);
